import pdfMake from "pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import {
  Attachable,
  AttachmentMeta,
  Contact,
  Equipment,
  EquipmentType,
  Facility,
  Fencing,
  Gate,
  GeoLocatable,
  KnowledgeBaseArticle,
  Manufacturer,
  PunchFacility,
  PunchItem,
  User
} from "@/survey";
import Vue from "vue";
import axios from "axios";
import { getAddressAsText, getFacilityHeaderFields, getUserHeaderFields } from "@/export/util";
import { getEquipmentType, getFacilityById, getFencingById } from "@/store";
import { equipmentTypeFormatter } from "@/components/formatters";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

interface MetaAndData {
  meta: AttachmentMeta;
  data: string;
}

interface PdfInlineStyle {
  bold?: boolean;
  italics?: boolean;
  decoration?: string;
  color?: string;
  link?: string;
}

// pdfmake can only embed PNG and JPEG data URLs. Anything else (remote URLs,
// gif/webp/svg) throws at render time, so unsupported sources are skipped.
const PDF_EMBEDDABLE_IMAGE = /^data:image\/(png|jpe?g);base64,/i;

export default class PdfGenerator {
  getBooleanClassFromBoolean(value = false): string {
    return `b${value}`;
  }

  fetchAndAddImages(
    promisesToWaitFor: Promise<any>[],
    items: Attachable[],
    imagesContent: any[],
    getName: (x: any) => string
  ) {
    items.forEach(item => {
      if (item.attachmentMeta && item.attachmentMeta.length > 0) {
        // dd.content.push({
        promisesToWaitFor.push(
          this.getImageData(item.attachmentMeta).then(imageUrls => {
            imagesContent.push({
              text: `${getName(item)} images`,
              style: "subheader",
              pageBreak: "before"
            });
            // this.addImagesToContent(imageUrls, dd, facility.name);
            this.addImagesToContent(imageUrls, imagesContent, getName(item));
            //dd.content.push({
            imagesContent.push({
              text: `End of ${getName(item)} images`,
              style: "subheader"
            });
          })
        );
      }
    });
  }

  tableWithHeaderRow(title: string, headerRow: any[], content: any[], pageSize = "A2", pageOrientation = "landscape"): any {
    const dd: any = {
      pageSize: pageSize,
      pageOrientation: pageOrientation,
      content: [
        {
          text: title,
          style: "header"
        },
        {
          columns: [
            { width: "*", text: "" },
            {
              width: "auto",
              style: "headerLineOnly",
              table: {
                headerRows: 1,
                body: [headerRow].concat(content)
              }
            }
          ]
        }
      ],
      styles: this.getStyles()
    };
    return dd;
  }

  simpleTable(title: string, headerLabels: string[], rows: string[][], pageSize = "A4", pageOrientation = "landscape"): Promise<unknown> {
    return new Promise(resolve => {
      const generated = new Date().toISOString().replace("T", " ").replace(/\.\d+Z$/, " UTC");
      const effectiveHeaderLabels = headerLabels.length > 0 ? headerLabels : [title];
      const columnCount = effectiveHeaderLabels.length;
      const headerRow = effectiveHeaderLabels.map(label => ({ text: this.normalizePdfCell(label), style: "reportTableHeader" }));
      const content = rows.map((row, rowIndex) => effectiveHeaderLabels.map((label, columnIndex) => {
        return {
          text: this.normalizePdfCell(row[columnIndex]),
          style: "reportTableCell",
          fillColor: rowIndex % 2 === 0 ? null : "#f7f9fc"
        };
      }));
      const dd: any = {
        pageSize,
        pageOrientation,
        pageMargins: [24, 56, 24, 34],
        header: () => ({
          margin: [24, 16, 24, 0],
          columns: [
            { text: title, style: "reportHeader" },
            { text: `${rows.length} records`, style: "reportHeaderMeta" }
          ]
        }),
        footer: (currentPage: number, pageCount: number) => ({
          margin: [24, 0, 24, 12],
          columns: [
            { text: `Generated ${generated}`, style: "reportFooter" },
            { text: `Page ${currentPage} of ${pageCount}`, style: "reportFooter", alignment: "right" }
          ]
        }),
        content: [
          {
            table: {
              headerRows: 1,
              widths: Array(columnCount).fill("*"),
              body: [headerRow].concat(content)
            },
            layout: {
              hLineColor: () => "#c8d0dc",
              vLineColor: () => "#d9e0e8",
              hLineWidth: (index: number) => index === 0 || index === 1 ? 0.8 : 0.4,
              vLineWidth: () => 0.4,
              paddingLeft: () => 4,
              paddingRight: () => 4,
              paddingTop: () => 3,
              paddingBottom: () => 3,
              fillColor: (rowIndex: number) => rowIndex === 0 ? "#dbe7f3" : null
            }
          }
        ],
        styles: this.getStyles(),
        defaultStyle: {
          fontSize: columnCount > 9 ? 7 : 8
        }
      };
      resolve(pdfMake.createPdf(dd));
    });
  }

  normalizePdfCell(value: string | number | null | undefined): string {
    if (value === null || value === undefined) {
      return "";
    }
    return `${value}`.replace(/\s+/g, " ").trim();
  }

  async getManufacturersTable(
    manufacturers: Manufacturer[],
    contacts: Contact[]
  ): Promise<unknown> {
    return new Promise(resolve => {
      const manufacturersTableArray = manufacturers.map(m => {
        const contact = contacts.reduce((acc: Contact, current: Contact) => {
          if (current.id === m.contact) {
            return current;
          }
          return acc;
        });
        return [
          this.getStyledText(`${m.company}`, "tableText"),
          this.getStyledText(`${m.website}`, "tableText"),
          this.getStyledText(
            `${contact.firstName} ${contact.lastName}`,
            "tableText"
          ),
          this.getStyledText(`${m.contactEmail}`, "tableText"),
          this.getStyledText(`${m.mainPhone}`, "tableText"),
          this.getStyledText(`${m.address1}\n${m.address2}`, "tableText"),
          this.getStyledText(`${m.city}, ${m.state} ${m.zip}`, "tableText")
        ];
      });
      const headerRow: any[] = [
        this.getStyledText("Company", "tableHeader"),
        this.getStyledText("Website", "tableHeader"),
        this.getStyledText("Contact", "tableHeader"),
        this.getStyledText("Contact Email", "tableHeader"),
        this.getStyledText("Main Phone", "tableHeader"),
        this.getStyledText("Address", "tableHeader"),
        this.getStyledText("Address", "tableHeader")
      ];

      resolve(
        pdfMake.createPdf(
          this.tableWithHeaderRow(
            "Manufacturers",
            headerRow,
            manufacturersTableArray
          )
        )
      );
    });
  }

  async getUsersTable(users: User[]): Promise<unknown> {
    return new Promise(resolve => {
      const usersTableArray = users.map(u => {
        return [
          this.getStyledText(`${u.username}`, "tableText"),
          this.getStyledText(`${u.firstName} ${u.lastName}`, "tableText"),
          this.getStyledText(`${u.email}`, "tableText"),
          this.getStyledText(`${u.phone}`, "tableText"),
          this.getStyledText(`${u.active}`, "tableText"),
          this.getStyledText(`${u.dateCreated}`, "tableText"),
          this.getStyledText(`${u.create}`, "tableText"),
          this.getStyledText(`${u.edit}`, "tableText"),
          this.getStyledText(`${u.view}`, "tableText"),
          this.getStyledText(`${u.delete}`, "tableText"),
          this.getStyledText(`${u.export}`, "tableText"),
          this.getStyledText(`${u.admin}`, "tableText")
        ];
      });
      const headerRow = getUserHeaderFields().map(f => {
        return this.getStyledText(f, "tableHeader");
      });
      resolve(
        pdfMake.createPdf(
          this.tableWithHeaderRow("Users", headerRow, usersTableArray)
        )
      );
    });
  }

  async getUserActivityTable(activity: any[]): Promise<unknown> {
    return new Promise(resolve => {
      const tableArray: any = [];
      activity.forEach((c: any) => {
        tableArray.push([
          this.getStyledText(c.user, "tableHeader"),
          this.getStyledText(c.action, "tableHeader"),
          this.getStyledText(c.objectType, "tableHeader"),
          this.getStyledText(this.formatDate(c.happenedTimestamp), "tableHeader")
        ]);
      });
      const headerRow = [
        // this.getStyledText("Id", "tableHeader"),
        this.getStyledText("User", "tableHeader"),
        this.getStyledText("Action", "tableHeader"),
        this.getStyledText("Object type", "tableHeader"),
        this.getStyledText("Date", "tableHeader")
      ];
      resolve(
        pdfMake.createPdf(
          this.tableWithHeaderRow("User Activity Report", headerRow, tableArray, "A4", "portrait")
        )
      );
    });
  }

  async getContactsTable(contacts: Contact[]): Promise<unknown> {
    return new Promise(resolve => {
      const contactTableArray: any = [];
      contacts.forEach((c: Contact) => {
        contactTableArray.push([
          this.getStyledText(`${c.firstName} ${c.lastName}`, "tableHeader"),
          this.getStyledText(c.email, "tableHeader"),
          this.getStyledText(c.phone, "tableHeader"),
          this.getStyledText(c.phone2, "tableHeader"),
          this.getStyledText(c.company, "tableHeader"),
          this.getStyledText(c.title, "tableHeader"),
          this.getStyledText(`${c.address1}\n${c.address2}`, "tableHeader"),
          this.getStyledText(`${c.city}, ${c.state} ${c.zip}`, "tableHeader"),
          this.getStyledText(c.contactType, "tableHeader"),
          this.getStyledText(c.notes, "tableHeader")
        ]);
      });
      const headerRow = [
        // this.getStyledText("Id", "tableHeader"),
        this.getStyledText("Name", "tableHeader"),
        this.getStyledText("Email", "tableHeader"),
        this.getStyledText("Phone", "tableHeader"),
        this.getStyledText("Phone 2", "tableHeader"),
        this.getStyledText("Company", "tableHeader"),
        this.getStyledText("Title", "tableHeader"),
        this.getStyledText("Address", "tableHeader"),
        this.getStyledText("Address", "tableHeader"),
        this.getStyledText("Type", "tableHeader"),
        this.getStyledText("Notes", "tableHeader")
      ];
      resolve(
        pdfMake.createPdf(
          this.tableWithHeaderRow("Contacts", headerRow, contactTableArray)
        )
      );
    });
  }

  async getFacilityTable(
    facilities: Facility[],
    contacts: Contact[],
    users: User[]
  ): Promise<unknown> {
    return new Promise(resolve => {
      const facilityTableArray = facilities.map(facility => {
        const contact = contacts.find((current: Contact) => current.id === facility.contact);
        const contactName = contact ? `${contact.firstName} ${contact.lastName}` : "";
        const exportBoolean = (value: boolean | null | undefined) => {
          return value === null || value === undefined ? "" : value.toString();
        };

        return [
          this.getStyledText(facility.facilityCode, "tableText"),
          this.getStyledText(facility.name, "tableText"),
          this.getStyledText(facility.aka, "tableText"),
          this.getStyledText(facility.surveyTeam, "tableText"),
          this.getStyledText(facility.surveyor, "tableText"),
          this.getStyledText(contactName, "tableText"),
          this.getStyledText(facility.status, "tableText"),
          this.getStyledText(facility.priority, "tableText"),
          this.getStyledText(facility.size, "tableText"),
          this.getStyledText(getAddressAsText(facility), "tableText"),
          this.getStyledText(facility.ophours, "tableText"),
          this.getStyledText(
            exportBoolean(facility.secstaffonsite),
            this.getBooleanClassFromBoolean(facility.secstaffonsite === true)
          ),
          this.getStyledText(
            exportBoolean(facility.visitorsignage),
            this.getBooleanClassFromBoolean(facility.visitorsignage === true)
          ),
          this.getStyledText(
            exportBoolean(facility.privpropsignage),
            this.getBooleanClassFromBoolean(facility.privpropsignage === true)
          ),
          this.getStyledText(
            exportBoolean(facility.lightingissues),
            this.getBooleanClassFromBoolean(facility.lightingissues === true)
          ),
          this.getStyledText(
            exportBoolean(facility.hasBathroom),
            this.getBooleanClassFromBoolean(facility.hasBathroom === true)
          ),
          this.getStyledText(
            exportBoolean(facility.asbestosSignage),
            this.getBooleanClassFromBoolean(facility.asbestosSignage === true)
          )
        ];
      });
      const headerRow = getFacilityHeaderFields().map(f => {
        return this.getStyledText(f, "tableHeader");
      });
      resolve(
        pdfMake.createPdf(
          this.tableWithHeaderRow("Facilities", headerRow, facilityTableArray)
        )
      );
    });
  }

  async generateReportReport(
    facilities: Facility[],
    equipment: Equipment[],
    fencing: Fencing[],
    gates: Gate[],
    contacts: Contact[],
    equipmentTypes: EquipmentType[]
  ): Promise<unknown> {
    return new Promise(resolve => {
      const date = new Date();
      const dd: any = {
        pageOrientation: "landscape",
        pageSize: "A2",
        content: [
          {
            text: `Priplan Report - ${this.padNumber(date.getMonth() + 1)}/${this.padNumber(date.getDate())}/${date.getFullYear()} ${date.getHours()}:${date.getMinutes()}`,
            style: "header"
          },
          {
            text: `(${equipment.length}) Equipment`,
            pageBreak: "before",
            style: "subheader"
          },
          {
            table: this.getEquipmentTable(equipment, equipmentTypes, facilities)
          },
          {
            text: `(${fencing.length}) Fencing`,
            pageBreak: "before",
            style: "subheader"
          },
          {
            table: this.getFencingTable(fencing, facilities)
          },
          {
            text: `(${gates.length}) Gates`,
            pageBreak: "before",
            style: "subheader"
          },
          {
            table: this.getGateTable(gates, facilities)
          }
        ],
        styles: this.getStyles()
      };
      const promisesToWaitFor = [] as any[];
      const equipmentImagesContent: any[] = [];
      const fencingImagesContent: any[] = [];
      const gateImagesContent: any[] = [];

      this.fetchAndAddImages(
        promisesToWaitFor,
        equipment,
        equipmentImagesContent,
        (x: any) => x.name
      );
      this.fetchAndAddImages(
        promisesToWaitFor,
        fencing,
        fencingImagesContent,
        (x: any) => x.name
      );
      this.fetchAndAddImages(
        promisesToWaitFor,
        gates,
        gateImagesContent,
        (x: any) => x.name
      );

      Promise.all(promisesToWaitFor).then(() => {
        dd.content = dd.content
          .concat(equipmentImagesContent)
          .concat(fencingImagesContent)
          .concat(gateImagesContent);
        resolve(pdfMake.createPdf(dd));
      });
    });
  }

  async generatePunchReport(facility: Facility, equipment: Equipment[], punchItems: PunchItem[], punchFacility: PunchFacility[])
    : Promise<unknown> {
    return new Promise(resolve => {
      const getEquipmentName = ((id: number) => {
        let ret = "Unknown";
        equipment.forEach(e => {
          if (e.id === id) {
            ret = e.name;
          }
        });
        return ret;
      });

      const piArray = punchItems.map((i: PunchItem) => {
        return [
          this.getStyledText(getEquipmentName(i.equipment), "tableText"),
          this.getStyledText(i.surveyor, "tableText"),
          this.getStyledText(this.formatDate(i.started + ""), "tableText"),
          this.getStyledText(this.formatDate(i.completed + ""), "tableText"),
          this.getStyledText(i.notes, "tableText"),
          i.tags!.map(t => t.tag)
        ];
      });
      const promisesToWaitFor = [] as Promise<any>[];
      const date = new Date();
      const dd: any = {
        pageOrientation: "landscape",
        content: [
          {
            text: `QAQC for ${facility.name} - ${
              facility.facilityCode
            } - ${this.padNumber(date.getMonth() + 1)}/${this.padNumber(date.getDate())}/${date.getFullYear()} ${date.getHours()}:${date.getMinutes()} Surveyor: ${facility.surveyor ||
            "Unknown"}`,
            style: "header"
          },
          {
            text: `${punchFacility.length > 0 && punchFacility[0].notes ? punchFacility[0].notes : "No QAQC notes for facility"}`,
            style: "header"
          },
          // {
          //   text: `${JSON.stringify(punchItems)}`
          // },
          {
            columns: [
              { width: "*", text: "" },
              {
                width: "auto",
                style: "headerLineOnly",
                table: {
                  headerRows: 1,
                  body: [
                    [
                      { text: "Equipment", style: "tableHeader" },
                      { text: "Surveyor", style: "tableHeader" },
                      { text: "Started", style: "tableHeader" },
                      { text: "Completed", style: "tableHeader" },
                      { text: "Notes", style: "tableHeader" },
                      { text: "Tags", stule: "tableHeader" }
                    ]
                  ].concat(
                    piArray as any
                  )
                }
              },
              { width: "*", text: "" }
            ]
          }
        ],
        styles: this.getStyles()
      };
      const punchItemsImagesContent = [] as any[];
      this.fetchAndAddImages(
        promisesToWaitFor,
        punchItems,
        punchItemsImagesContent,
        (x: any) => getEquipmentName(x.equipment)
      );
      Promise.all(promisesToWaitFor).then(() => {
        dd.content = dd.content
          .concat({
            text: `QAQC represents site visit by consultant to determine and document specific device checklists and general workmanship, and does not represent a comprehensive review of completeness of installation, functional testing, or contract compliance or performance.`,
            // pageBreak: "before",
            style: "disclaimer"
          })
          .concat(punchItemsImagesContent);
        // .concat(facilityImagesContent)
        // .concat(equipmentImagesContent)
        // .concat(fencingImagesContent)
        // .concat(gateImagesContent);
        resolve(pdfMake.createPdf(dd));
      });
    });
  }

  async generateFacilityReport(
    facility: Facility,
    equipment: Equipment[],
    fencing: Fencing[],
    gates: Gate[],
    contacts: Contact[],
    equipmentTypes: EquipmentType[]
  ): Promise<unknown> {
    let c: Contact;
    contacts.forEach(contact => {
      if (contact.id === facility.contact) {
        c = contact;
      }
    });
    const date = new Date();

    // let equipmentMapData: any = {
    //   text: `No map available`,
    //   style: "header"
    // };
    // try {
    //     equipmentMapData = {
    //     image: await this.getStaticMapByGeoLocatable(facility),
    //     width: 600
    //   };
    // } catch (e) {
    //   alert("Error getting map from Google");
    // }

    let facilityMapData: any = {
      text: `No map available`,
      style: "header"
    };
    try {
      facilityMapData = {
        image: await this.getStaticMapByGeoLocatable(facility),
        width: 600
      };
    } catch (e) {
      console.error("Error getting map from Google");
    }
    return new Promise(resolve => {
      const dd: any = {
        pageOrientation: "landscape",
        content: [
          {
            text: `${facility.name} - ${
              facility.facilityCode
            } ${facility.aka ? `(${facility.aka})` : ""} - ${this.padNumber(date.getMonth() + 1)}/${this.padNumber(date.getDate())}/${date.getFullYear()} ${date.getHours()}:${date.getMinutes()} Surveyor: ${facility.surveyor ||
            "Unknown"}`,
            style: "header"
          },
          {
            text: facility.description,
            style: "subheader"
          },
          {
            columns: [
              { width: "*", text: "" },
              {
                width: "auto",
                style: "headerLineOnly",
                table: {
                  headerRows: 1,
                  body: [
                    [
                      { text: "Contact", style: "tableHeader" },
                      { text: "Address", style: "tableHeader" },
                      { text: "Site", style: "tableHeader" },
                      {
                        text: "Attributes",
                        style: "tableHeader"
                      }
                    ],
                    [
                      {
                        text:
                          c === undefined
                            ? `Unknown`
                            : `${c.firstName} ${c.lastName}\n${c.email}\n${c.phone}`,
                        style: "tableCell"
                      },
                      {
                        text: getAddressAsText(facility),
                        style: "tableCell"
                      },
                      {
                        table: {
                          body: [
                            [{ text: "Status", bold: true }, facility.status],
                            [
                              { text: "Priority", bold: true },
                              facility.priority
                            ],
                            [{ text: "Size", bold: true }, facility.size]
                          ]
                        },
                        style: "tableCell"
                      },
                      {
                        table: {
                          body: [
                            [
                              { text: "Security Staff", bold: true },
                              {
                                text: facility.secstaffonsite,
                                style: this.getBooleanClassFromBoolean(
                                  facility.secstaffonsite
                                )
                              }
                            ],
                            [
                              { text: "Visitor Signage", bold: true },
                              {
                                text: facility.visitorsignage,
                                style: this.getBooleanClassFromBoolean(
                                  facility.visitorsignage
                                )
                              }
                            ],
                            [
                              { text: "Private Property Signage", bold: true },
                              {
                                text: facility.privpropsignage,
                                style: this.getBooleanClassFromBoolean(
                                  facility.privpropsignage
                                )
                              }
                            ],
                            [
                              { text: "Lighting Issues", bold: true },
                              {
                                text: facility.lightingissues,
                                style: this.getBooleanClassFromBoolean(
                                  facility.lightingissues
                                )
                              }
                            ],
                            [
                              { text: "Hours", bold: true },
                              {
                                text: facility.ophours
                              }
                            ]
                          ],
                          style: "tableCell"
                        }
                      }
                    ]
                  ]
                }
              },
              { width: "*", text: "" }
            ] // End columns
          },
          {
            text: `Coordinates: (${
              facility.lat && facility.lng
                ? facility.lat + ", " + facility.lng
                : "Unknown"
            })`,
            style: "subheader"
          },
          {
            text: "Notes",
            style: "subheader"
          },
          {
            text: facility.notes ? facility.notes : "None.",
            style: "text"
          },
          facilityMapData,
          {
            text: `(${equipment.length}) Equipment for ${facility.name}`,
            pageBreak: "before",
            style: "subheader"
          },
          {
            table: this.getEquipmentTable(equipment, equipmentTypes)
          },
          {
            text: `(${fencing.length}) Fencing for ${facility.name}`,
            pageBreak: "before",
            style: "subheader"
          },
          {
            table: this.getFencingTable(fencing)
          },
          {
            text: `(${gates.length}) Gates for ${facility.name}`,
            pageBreak: "before",
            style: "subheader"
          },
          {
            table: this.getGateTable(gates)
          }
        ],
        styles: this.getStyles()
      };

      const promisesToWaitFor = [] as any[];
      const facilityImagesContent: any[] = [];
      const equipmentImagesContent: any[] = [];
      const fencingImagesContent: any[] = [];
      const gateImagesContent: any[] = [];

      this.fetchAndAddImages(
        promisesToWaitFor,
        [facility],
        facilityImagesContent,
        (x: any) => x.name
      );
      this.fetchAndAddImages(
        promisesToWaitFor,
        equipment,
        equipmentImagesContent,
        (x: any) => x.name
      );
      this.fetchAndAddImages(
        promisesToWaitFor,
        fencing,
        fencingImagesContent,
        (x: any) => x.name
      );
      this.fetchAndAddImages(
        promisesToWaitFor,
        gates,
        gateImagesContent,
        (x: any) => x.name
      );

      Promise.all(promisesToWaitFor).then(() => {
        dd.content = dd.content
          .concat(facilityImagesContent)
          .concat(equipmentImagesContent)
          .concat(fencingImagesContent)
          .concat(gateImagesContent);
        resolve(pdfMake.createPdf(dd));
      });
    });
  }

  getStyledText(text: string | null | undefined | number, style: string) {
    if (text === null || text === undefined) {
      text = "";
    }
    return { text: `${text}`, style };
  }

  padNumber(num: number) {
    return num > 9 ? "" + num : "0" + num;
  }

  formatDate(dateString: string | null): string {
    if (dateString === null) {
      return "Unknown";
    }
    const t = parseInt(dateString);
    if (Number.isNaN(t)) {
      return "";
    }
    const date = new Date(t);
    const d = date.getDate();
    const m = date.getMonth() + 1; //Month from 0 to 11
    const y = date.getFullYear();
    return "" + y + "-" + (m <= 9 ? "0" + m : m) + "-" + (d <= 9 ? "0" + d : d);
  }

  getGateTable(gates: Gate[], facilities?: Facility[]): any {
    let showFacility = false;
    let colspanOffset = 0;
    if (facilities !== undefined && facilities.length > 0) {
      showFacility = true;
      colspanOffset = 1;
    }
    if (gates.length === 0) {
      return {
        headerRows: 1,
        body: [[this.getStyledText("No Gates", "tableHeader")]]
      };
    }
    const gateArray: any[] = [];
    gates.forEach((g: Gate) => {
      const toAdd = [
        this.getStyledText(g.name, "tableText"),
        this.getStyledText(`${g.surveyTeam}`, "tableText"),
        this.getStyledText(g.description, "tableText"),
        this.getStyledText(g.gatetype, "tableText"),
        this.getStyledText(g.optype, "tableText"),
        this.getStyledText(g.acctype, "tableText")
      ];
      if (showFacility) {
        const fencing = getFencingById(g.fencing);
        const facility = getFacilityById(fencing?.facility!);
        toAdd.splice(1, 0, this.getStyledText(facility!.name, "tableText"));
      }
      gateArray.push(toAdd);
      if (g.notes && g.notes.length > 0) {
        gateArray.push([
          this.getStyledText("Notes", "tableHeader"),
          { text: g.notes, style: "tableText", colSpan: 5 + colspanOffset }
        ]);
      }
      gateArray.push([
        { text: "", colSpan: 6 + colspanOffset, style: "greyFill" }
      ]);
    });
    const headerRow = [
      // Header
      this.getStyledText("Name", "tableHeader"),
      this.getStyledText("Survey Team", "tableHeader"),
      this.getStyledText("Description", "tableHeader"),
      this.getStyledText("Gate Type", "tableHeader"),
      this.getStyledText("Operation", "tableHeader"),
      this.getStyledText("Access Control", "tableHeader")
    ];
    if (showFacility) {
      headerRow.splice(1, 0, this.getStyledText("Facility", "tableHeader"));
    }
    const table = {
      headerRows: 1,
      body: [headerRow].concat(gateArray)
    };
    return table;
  }

  getFencingTable(fencing: Fencing[], facilities?: Facility[]): any {
    if (fencing.length === 0) {
      return {
        headerRows: 1,
        body: [[this.getStyledText("No Fencing", "tableHeader")]]
      };
    }

    let showFacility = false;
    let colspanOffset = 0;
    if (facilities !== undefined && facilities.length > 0) {
      showFacility = true;
      colspanOffset = 1;
    }
    const fencingArray: any[] = [];
    fencing.forEach(f => {
      const toAdd = [
        this.getStyledText(f.name, "tableText"),
        this.getStyledText(`${f.surveyTeam}`, "tableText"),
        this.getStyledText(
          `${f.isfenced || false}`,
          this.getBooleanClassFromBoolean(f.isfenced || false)
        ),
        this.getStyledText(
          `${f.fencedamaged || false}`,
          this.getBooleanClassFromBoolean(!f.fencedamaged)
        ),
        this.getStyledText(
          `${f.vegproblem || false}`,
          this.getBooleanClassFromBoolean(!f.vegproblem)
        ),
        this.getStyledText(
          `${f.climbingaids || false}`,
          this.getBooleanClassFromBoolean(!f.climbingaids)
        )
      ];
      if (showFacility) {
        const facility = getFacilityById(f.id!);
        toAdd.splice(1, 0, this.getStyledText(facility!.name, "tableText"));
      }
      fencingArray.push(toAdd);
      if (f.notes && f.notes.length > 0) {
        fencingArray.push([
          this.getStyledText("Notes", "tableHeader"),
          { text: f.notes, style: "tableText", colSpan: 5 + colspanOffset }
        ]);
      }
      fencingArray.push([
        { text: "", colSpan: 6 + colspanOffset, style: "greyFill" }
      ]);
    });
    const headerRow = [
      // Header
      this.getStyledText("Name", "tableHeader"),
      this.getStyledText("Survey Team", "tableHeader"),
      this.getStyledText("Fenced", "tableHeader"),
      this.getStyledText("Damaged", "tableHeader"),
      this.getStyledText("Vegetation", "tableHeader"),
      this.getStyledText("Climbing Aids", "tableHeader")
    ];
    if (showFacility) {
      headerRow.splice(1, 0, this.getStyledText("Facility", "tableHeader"));
    }
    const table = {
      headerRows: 1,
      body: [headerRow].concat(fencingArray as any)
    };
    return table;
  }

  getEquipmentTable(
    equipment: Equipment[],
    equipmentTypes: EquipmentType[],
    facilities?: Facility[]
  ): any {
    if (equipment.length === 0) {
      return {
        headerRows: 1,
        body: [[this.getStyledText("No Equipment", "tableHeader")]]
      };
    }
    const equipmentArray: any[] = [];
    let showFacility = false;
    let colspanOffset = 0;
    if (facilities !== undefined && facilities.length > 0) {
      showFacility = true;
      colspanOffset = colspanOffset + 1;
    }
    const equipmentTypeRenderer = equipmentTypeFormatter(equipmentTypes);
    equipment.forEach(equipment => {
      const toAdd = [
        this.getTypeInfo(equipment, equipmentTypeRenderer)
        ,
        this.getStyledText(
          [equipment.name, equipment.standardizedName].join("\n\n"),
          "tableText"
        ),
        this.getStyledText(
          equipment.surveyTeam === null ? "" : equipment.surveyTeam,
          "tableText"
        ),
        this.getStyledText(
          `${equipment.surveyed === true ? "Y" : "N"}`,
          this.getBooleanClassFromBoolean(equipment.surveyed === true)
        ),
        this.getStyledText(equipment.description, "tableText"),
        this.getStyledText(
          [equipment.modelNo, equipment.serialNo].join("\n\n"),
          "tableText"
        ),
        this.getStyledText(
          [
            // equipment.ipAddress || "No IP",
            equipment.macAddress || "No MAC",
            equipment.lat && equipment.lng
              ? "(" + equipment.lat + ", " + equipment.lng + ")"
              : ""
          ].join("\n\n"),
          "tableText"
        ),
        this.getStyledText(equipment.location, "tableText"),
        this.getStyledText([this.formatDate(equipment.installDate), equipment.vendor].join("\n\n"), "tableText"),
        // this.getStyledText(equipment.vendor, "tableText"),
        this.getStyledText(
          `${equipment.isLabeled || false}`,
          this.getBooleanClassFromBoolean(equipment.isLabeled || false)
        ),
        this.getDoorInfo(equipment)
      ];
      if (showFacility) {
        const facility = getFacilityById(equipment.facility!);
        toAdd.splice(1, 0, this.getStyledText(facility!.name, "tableText"));
      }
      equipmentArray.push(toAdd);
      if (equipment.notes && equipment.notes.length > 0) {
        equipmentArray.push([
          this.getStyledText("Notes", "tableHeader"),
          {
            text: equipment.notes,
            style: "tableText",
            colSpan: 10 + colspanOffset
          }
        ]);
      }
      equipmentArray.push([
        { text: "", colSpan: 11 + colspanOffset, style: "greyFill" }
      ]);
    });

    const headerRow = [
      // Header
      this.getStyledText("Type", "tableHeader"),
      this.getStyledText("Name", "tableHeader"),
      this.getStyledText("Survey Team", "tableHeader"),
      this.getStyledText("Surveyed", "tableHeader"),
      this.getStyledText("Description", "tableHeader"),
      this.getStyledText("Model/Serial", "tableHeader"),
      this.getStyledText("IP/MAC", "tableHeader"),
      this.getStyledText("Location", "tableHeader"),
      this.getStyledText("Installed/Installer", "tableHeader"),
      // this.getStyledText("Installer", "tableHeader"),
      this.getStyledText("Labeled", "tableHeader"),
      this.getStyledText("Door", "tableHeader")
    ]; // End header
    if (showFacility) {
      headerRow.splice(1, 0, this.getStyledText("Facility", "tableHeader"));
    }
    const table = {
      headerRows: 1,
      body: [headerRow].concat(equipmentArray as any)
    };
    console.log("Equipment table", table);
    console.log(JSON.stringify(table, null, 2));
    return table;
  }

  async getEquipmentListTable(
    equipment: Equipment[],
    equipmentTypes: EquipmentType[],
    facilities?: Facility[]
  ): Promise<unknown> {
    return new Promise(resolve => {
      const dd: any = {
        pageSize: "A2",
        pageOrientation: "landscape",
        content: [
          {
            text: "Equipment",
            style: "header"
          },
          {
            table: this.getEquipmentTable(equipment, equipmentTypes, facilities)
          }
        ],
        styles: this.getStyles()
      };
      resolve(pdfMake.createPdf(dd));
    });
  }

  getTypeInfo(equipment: Equipment, equipmentTypeRenderer: any): any {
    if (getEquipmentType(equipment.equipmentType).name !== "Node") {
      return this.getStyledText(
        equipmentTypeRenderer(equipment.equipmentType),
        "tableText"
      );
    }
    const table = {
      table: {
        body: [
          ["Node"],
          [
            this.getStyledText(
              `AC PF Alarm`,
              this.getBooleanClassFromBoolean(
                equipment.acPowerFailAlarm === 2 || false
              )
            )
          ],
          [
            this.getStyledText(
              `Battery`,
              this.getBooleanClassFromBoolean(
                equipment.batteryInstalled === 2 || false
              )
            )
          ],
          [
            this.getStyledText(
              `Low bat Alarm`,
              this.getBooleanClassFromBoolean(
                equipment.lowBatteryAlarm === 2 || false
              )
            )
          ]
        ]
      }
    };
    if (equipment.batteryInstalled === 2) {
      table.table.body.push([this.getStyledText(this.formatDate(equipment.batteryInstallDate), "tableText")]);
    }
    return table;
  }

  getDoorInfo(equipment: Equipment): any {
    if (!(equipment.door === 2)) {
      return this.getStyledText("Not door", "tableText");
    } else {
      return {
        table: {
          body: [
            [equipment.exterior === 2 ? "Exterior" : "Interior"],
            [equipment.public === 2 ? "Public" : "Not Public"],
            [
              this.getStyledText(
                `Valid read`,
                this.getBooleanClassFromBoolean(
                  equipment.validRead === 2 || false
                )
              )
            ],
            [
              this.getStyledText(
                `Forced`,
                this.getBooleanClassFromBoolean(
                  equipment.doorForced === 2 || false
                )
              )
            ],
            [
              this.getStyledText(
                `Held`,
                this.getBooleanClassFromBoolean(
                  equipment.doorHeld === 2 || false
                )
              )
            ],
            [
              this.getStyledText(
                `REX`,
                this.getBooleanClassFromBoolean(
                  equipment.doorRex === 2 || false
                )
              )
            ]
          ]
        }
      };
    }
  }

  addImagesToContent(imageUrls: MetaAndData[], dd: any, caption: string): void {
    const cols = 1;
    const colWidth = 750 / cols;
    let start = 0;
    let end = cols;

    let arr = imageUrls.slice(start, end);
    while (arr.length > 0) {
      // dd.content.push({
      dd.push({
        style: "text",
        unbreakable: true,
        table: {
          body: [
            arr.map((img) => {
              return [
                img.meta.notes ? img.meta.notes : "No comment",
                {
                  image: img.data,
                  fit: [600, 600]
                },
                caption
              ];
            })
          ]
        }
      });
      start = start + cols;
      end = end + cols;
      arr = imageUrls.slice(start, end);
    }
  }

  async getStaticMapByGeoLocatable(
    geoLocatable: GeoLocatable
  ): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const url = `${Vue.prototype.$apiLocation}maps?lat=${geoLocatable.lat}&lng=${geoLocatable.lng}&height=800&width=800`;
      return axios
        .get<any>(url, {
          responseType: "arraybuffer"
        })
        .then(data => {
          const image = Buffer.from(data.data, "binary").toString("base64");
          const contentType = String(data.headers["content-type"] ?? "");
          if (contentType.indexOf("image") > -1) {
            resolve(`data:${contentType};base64,${image}`);
          } else {
            reject(`Invalid content type: ${contentType}`);
          }
        })
        .catch(error => alert("Cannot fetch map for report!"));
    });
  }

  // async getStaticMapByAddress(addressable: Addressable): Promise<any> {
  //     <img src="https://maps.googleapis.com/maps/api/staticmap?center=Berkeley,CA&marker=center=Berkeley,CA&zoom=14&size=400x400&key=AIzaSyC6ygSFTWeIKAXmpYHsqQ1403C3TQQF-Bw"/>
  //     <img src=""/>
  // }

  async getImageData(attachmentMeta: AttachmentMeta[]): Promise<MetaAndData[]> {
    return new Promise<MetaAndData[]>((resolve, reject) => {
      const imageUrls: MetaAndData[] = [];
      let promises: Promise<any>[] = [];

      try {
        promises = attachmentMeta.map(meta => {
          return axios
            .get<any>(`${Vue.prototype.$apiLocation}attach/${meta.id}?table=${meta.tablename}`, {
              responseType: "arraybuffer"
            })
            .then(data => {
              const image = Buffer.from(data.data, "binary").toString("base64");
              const contentType = String(data.headers["content-type"] ?? "");
              if (contentType.indexOf("image") > -1) {
                imageUrls.push({
                  meta,
                  data: `data:${contentType};base64,${image}`
                });
              }
            });
        });
        Promise.all(promises).then(() => resolve(imageUrls));
      } catch (e) {
        reject(e);
      }
    });
  }

  async getKnowledgeBaseArticleDocument(article: KnowledgeBaseArticle): Promise<any> {
    const title = (article.title || "").trim() || "Knowledge Base Article";
    const generated = new Date().toISOString().replace("T", " ").replace(/\.\d+Z$/, " UTC");

    const metaParts: string[] = [article.categoryName || "Uncategorized"];
    if (article.status && article.status !== "published") {
      metaParts.push(article.status.toUpperCase());
    }
    if (article.authorName) {
      metaParts.push(`By ${article.authorName}`);
    }
    if (article.updatedAt) {
      metaParts.push(`Updated ${new Date(article.updatedAt).toLocaleString()}`);
    }

    const content: any[] = [
      { text: title, style: "kbTitle" },
      { text: metaParts.join("   \u00b7   "), style: "kbMeta" },
      {
        canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.8, lineColor: "#c8d0dc" }],
        margin: [0, 6, 0, 12]
      }
    ];

    const summary = (article.summary || "").trim();
    if (summary.length > 0) {
      content.push({ text: summary, style: "kbSummary" });
    }

    const body = this.knowledgeBaseContentToPdf(article.content || "");
    if (body.length > 0) {
      content.push(...body);
    } else {
      content.push({ text: "This article has no content.", style: "kbEmpty" });
    }

    const tags = `${article.tags || ""}`.split(",").map(tag => tag.trim()).filter(tag => tag.length > 0);
    if (tags.length > 0) {
      content.push({ text: `Tags: ${tags.join(", ")}`, style: "kbTags", margin: [0, 14, 0, 0] });
    }

    const attachments = (article.attachmentMeta || []).filter(item => item.notes !== "Pasted into article body");
    if (attachments.length > 0) {
      content.push({ text: "Attachments", style: "kbSection", pageBreak: "before" });

      const nonImages = attachments.filter(item => (item.contenttype || "").indexOf("image/") !== 0);
      if (nonImages.length > 0) {
        content.push({
          ul: nonImages.map(item => this.normalizePdfCell(item.name || item.originalfilename) || "Attachment"),
          style: "kbParagraph"
        });
      }

      const images = attachments.filter(item => (item.contenttype || "").indexOf("image/") === 0);
      const imageData = await this.getKnowledgeBaseAttachmentImages(images);
      imageData.forEach(item => {
        content.push({
          text: this.normalizePdfCell(item.meta.name || item.meta.originalfilename) || "Image",
          style: "kbCaption"
        });
        content.push({ image: item.data, fit: [470, 470], margin: [0, 0, 0, 14] });
      });
    }

    const dd: any = {
      pageSize: "LETTER",
      pageOrientation: "portrait",
      pageMargins: [40, 40, 40, 40],
      info: { title },
      footer: (currentPage: number, pageCount: number) => ({
        margin: [40, 0, 40, 12],
        columns: [
          { text: `${title} · Generated ${generated}`, style: "reportFooter" },
          { text: `Page ${currentPage} of ${pageCount}`, style: "reportFooter", alignment: "right" }
        ]
      }),
      content,
      styles: this.getStyles(),
      defaultStyle: { fontSize: 10 }
    };
    return pdfMake.createPdf(dd);
  }

  // Each request carries its own catch so one failed attachment cannot reject
  // Promise.all and leave the export hanging forever.
  async getKnowledgeBaseAttachmentImages(attachments: AttachmentMeta[]): Promise<MetaAndData[]> {
    const results = await Promise.all(
      attachments.map(meta => {
        const table = encodeURIComponent(meta.tablename || "kb_article");
        return axios
          .get<any>(`${Vue.prototype.$apiLocation}attach/${meta.id}?table=${table}`, { responseType: "arraybuffer" })
          .then(response => {
            const contentType = String(response.headers["content-type"] ?? "");
            if (contentType.indexOf("image/") !== 0) {
              return null;
            }
            const image = Buffer.from(response.data, "binary").toString("base64");
            const data = `data:${contentType};base64,${image}`;
            return PDF_EMBEDDABLE_IMAGE.test(data) ? { meta, data } : null;
          })
          .catch(() => null);
      })
    );
    return results.filter((item): item is MetaAndData => item !== null);
  }

  knowledgeBaseContentToPdf(content: string): any[] {
    const source = this.normalizeArticleContent(content);
    if (source.length === 0) {
      return [];
    }
    const template = document.createElement("template");
    template.innerHTML = source;
    template.content.querySelectorAll("script, style, iframe, object, embed").forEach(element => element.remove());
    const blocks: any[] = [];
    this.appendArticleBlocks(Array.from(template.content.childNodes), blocks, {});
    return blocks;
  }

  normalizeArticleContent(content: string): string {
    const value = (content || "").trim();
    if (value.length === 0) {
      return "";
    }
    if (/<[a-z][\s\S]*>/i.test(value)) {
      return value;
    }
    return value
      .split(/\n{2,}/)
      .map(paragraph => paragraph.trim())
      .filter(paragraph => paragraph.length > 0)
      .map(paragraph => `<p>${this.escapeArticleHtml(paragraph).replace(/\n/g, "<br>")}</p>`)
      .join("");
  }

  escapeArticleHtml(content: string): string {
    return content
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  appendArticleBlocks(nodes: Node[], blocks: any[], style: PdfInlineStyle): void {
    let pending: any[] = [];
    const flush = () => {
      const runs = pending;
      pending = [];
      if (runs.length === 0) {
        return;
      }
      const hasText = runs.some(run => typeof run.text === "string" && run.text.trim().length > 0);
      if (hasText) {
        blocks.push({ text: runs, style: "kbParagraph" });
      }
    };

    nodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || "";
        if (text.length > 0) {
          pending.push({ text, ...style });
        }
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }

      const element = node as HTMLElement;
      const tag = element.tagName.toLowerCase();

      switch (tag) {
        case "br":
          pending.push({ text: "\n" });
          break;
        case "img": {
          flush();
          const image = this.articleImage(element.getAttribute("src"));
          if (image !== null) {
            blocks.push(image);
          }
          break;
        }
        case "hr":
          flush();
          blocks.push({
            canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: "#c8d0dc" }],
            margin: [0, 6, 0, 10]
          });
          break;
        case "h1":
        case "h2":
        case "h3":
        case "h4":
        case "h5":
        case "h6": {
          flush();
          const text = (element.textContent || "").trim();
          if (text.length > 0) {
            const level = parseInt(tag.substring(1), 10);
            blocks.push({ text, style: level <= 2 ? "kbHeading1" : "kbHeading2" });
          }
          break;
        }
        case "ul":
        case "ol": {
          flush();
          const items: any[] = [];
          Array.from(element.children).forEach(child => {
            if (child.tagName.toLowerCase() !== "li") {
              return;
            }
            const itemBlocks: any[] = [];
            this.appendArticleBlocks(Array.from(child.childNodes), itemBlocks, style);
            if (itemBlocks.length === 0) {
              items.push({ text: "" });
            } else if (itemBlocks.length === 1) {
              items.push(itemBlocks[0]);
            } else {
              items.push({ stack: itemBlocks });
            }
          });
          if (items.length > 0) {
            blocks.push({ [tag]: items, margin: [0, 0, 0, 8] });
          }
          break;
        }
        case "blockquote": {
          flush();
          const text = (element.textContent || "").trim();
          if (text.length > 0) {
            blocks.push({ text, style: "kbQuote", margin: [18, 0, 0, 8] });
          }
          break;
        }
        case "pre": {
          flush();
          const text = element.textContent || "";
          if (text.trim().length > 0) {
            blocks.push({ text, style: "kbCode", margin: [12, 0, 0, 8] });
          }
          break;
        }
        case "table": {
          flush();
          const table = this.articleTable(element);
          if (table !== null) {
            blocks.push(table);
          }
          break;
        }
        case "p":
        case "div":
        case "li":
        case "section":
        case "article":
        case "header":
        case "footer":
        case "main":
        case "aside":
        case "figure":
        case "figcaption":
          flush();
          this.appendArticleBlocks(Array.from(element.childNodes), blocks, style);
          break;
        default:
          pending.push(...this.articleInlineRuns(element, style));
          break;
      }
    });

    flush();
  }

  articleInlineRuns(element: HTMLElement, style: PdfInlineStyle): any[] {
    const tag = element.tagName.toLowerCase();
    const next: PdfInlineStyle = { ...style };

    if (tag === "b" || tag === "strong") {
      next.bold = true;
    }
    if (tag === "i" || tag === "em") {
      next.italics = true;
    }
    if (tag === "u" || tag === "ins") {
      next.decoration = "underline";
    }
    if (tag === "s" || tag === "strike" || tag === "del") {
      next.decoration = "lineThrough";
    }
    if (tag === "a") {
      next.color = "#1f6fb2";
      next.decoration = "underline";
      const href = element.getAttribute("href");
      // Only http(s) links are carried into the PDF; javascript: and other
      // schemes are dropped so the export cannot become a clickable payload.
      if (href !== null && /^https?:\/\//i.test(href.trim())) {
        next.link = href.trim();
      }
    }

    const runs: any[] = [];
    Array.from(element.childNodes).forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || "";
        if (text.length > 0) {
          runs.push({ text, ...next });
        }
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }
      const child = node as HTMLElement;
      if (child.tagName.toLowerCase() === "br") {
        runs.push({ text: "\n" });
        return;
      }
      runs.push(...this.articleInlineRuns(child, next));
    });
    return runs;
  }

  articleImage(src: string | null): any | null {
    if (src === null || !PDF_EMBEDDABLE_IMAGE.test(src.trim())) {
      return null;
    }
    return { image: src.trim(), fit: [470, 470], margin: [0, 6, 0, 10] };
  }

  articleTable(element: HTMLElement): any | null {
    const rows = Array.from(element.querySelectorAll("tr"));
    if (rows.length === 0) {
      return null;
    }
    const body = rows.map(row =>
      Array.from(row.querySelectorAll("th, td")).map(cell => ({
        text: this.normalizePdfCell(cell.textContent || ""),
        style: cell.tagName.toLowerCase() === "th" ? "kbTableHeader" : "kbTableCell"
      }))
    );
    const columnCount = body.reduce((max, row) => Math.max(max, row.length), 0);
    if (columnCount === 0) {
      return null;
    }
    body.forEach(row => {
      while (row.length < columnCount) {
        row.push({ text: "", style: "kbTableCell" });
      }
    });
    return {
      table: {
        headerRows: rows[0].querySelector("th") !== null ? 1 : 0,
        widths: Array(columnCount).fill("*"),
        body
      },
      layout: {
        hLineColor: () => "#c8d0dc",
        vLineColor: () => "#d9e0e8",
        hLineWidth: () => 0.4,
        vLineWidth: () => 0.4,
        paddingLeft: () => 5,
        paddingRight: () => 5,
        paddingTop: () => 4,
        paddingBottom: () => 4
      },
      margin: [0, 4, 0, 10]
    };
  }

  getStyles() {
    return {
      disclaimer: {
        fontSize: 10,
        marginBottom: 5,
        marginTop: 20,
        fontColor: "red",
        bold: true
      },
      headerLineOnly: {
        alignment: 'center'
      },
      header: {
        fontSize: 18,
        marginBottom: 5,
        bold: true,
        alignment: 'center'
      },
      reportHeader: {
        fontSize: 13,
        bold: true,
        color: "#1f3f63"
      },
      reportHeaderMeta: {
        fontSize: 8,
        color: "#4d5a68",
        alignment: "right"
      },
      reportFooter: {
        fontSize: 7,
        color: "#66717f"
      },
      reportTableHeader: {
        bold: true,
        fontSize: 8,
        color: "#1f2d3d"
      },
      reportTableCell: {
        fontSize: 7.5,
        color: "#1f2d3d"
      },
      subheader: {
        fontSize: 15,
        marginBottom: 5,
        bold: true
      },
      text: {
        marginBottom: 5
      },
      quote: {
        italics: true
      },
      small: {
        fontSize: 8
      },
      tableHeader: {
        bold: true,
        fontSize: 13,
        color: "black"
      },
      tableCell: {
        padding: 10
      },
      btrue: {
        fillColor: "#00FF00"
      },
      bfalse: {
        fillColor: "#FF0000"
      },
      greyFill: {
        fillColor: "#999999"
      },
      kbTitle: {
        fontSize: 20,
        bold: true,
        color: "#1f3f63",
        marginBottom: 4
      },
      kbMeta: {
        fontSize: 8.5,
        color: "#66717f"
      },
      kbSummary: {
        fontSize: 11,
        italics: true,
        color: "#3d4b5a",
        marginBottom: 10
      },
      kbHeading1: {
        fontSize: 14,
        bold: true,
        color: "#1f3f63",
        marginTop: 8,
        marginBottom: 4
      },
      kbHeading2: {
        fontSize: 12,
        bold: true,
        color: "#1f3f63",
        marginTop: 6,
        marginBottom: 3
      },
      kbParagraph: {
        fontSize: 10,
        color: "#1f2d3d",
        lineHeight: 1.35,
        marginBottom: 6
      },
      kbQuote: {
        fontSize: 10,
        italics: true,
        color: "#4d5a68"
      },
      kbCode: {
        fontSize: 9,
        color: "#26313f",
        preserveLeadingSpaces: true
      },
      kbTags: {
        fontSize: 9,
        color: "#4d5a68",
        italics: true
      },
      kbSection: {
        fontSize: 14,
        bold: true,
        color: "#1f3f63",
        marginBottom: 8
      },
      kbCaption: {
        fontSize: 9,
        bold: true,
        color: "#4d5a68",
        marginBottom: 3
      },
      kbEmpty: {
        fontSize: 10,
        italics: true,
        color: "#66717f"
      },
      kbTableHeader: {
        fontSize: 9,
        bold: true,
        color: "#1f2d3d"
      },
      kbTableCell: {
        fontSize: 9,
        color: "#1f2d3d"
      }
    };
  }
}
